import {
  CategoryItemKey,
  useLearnerCategories
} from '../hooks/useLearnerCategories';
import {
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';

import AuthService from '../../shared/services/AuthService';
import Button from '@mui/material/Button';
import { CategoryTypeEnum } from '../../siteadmin/models/CategoryAsset';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ErrorPanel from '../../shared/components/ErrorPanel';
import Grid from '@mui/material/Unstable_Grid2';
import { cloneDeep } from 'lodash';
import { useLocation } from 'react-router-dom';
import usePathways from '../hooks/usePathways';
import useThemes from '../../shared/hooks/useThemes';

interface UserOptionStatus {
  categoryUserOptionStatus: boolean;
}
interface IUserGroupSelection {
  open: boolean;
  setDialogOpen: (value: boolean) => void;
}

export const UserGroupSelection = (props: IUserGroupSelection) => {
  const { muiTheme } = useThemes();
  const main = muiTheme.palette.primary.main;
  const location = useLocation();
  const userkey = AuthService.Userkey;

  const [singleSelectKey, setSingleSelectKey] = useState<
    CategoryItemKey | undefined
  >(undefined);
  const [multipleSelectKeys, setMultipleSelectKeys] = useState<
    CategoryItemKey[]
  >([]);

  const { getPathways } = usePathways();
  const {
    allCategories,
    loadingCategories,
    loadError,
    saveSelection,
    saving,
    saveError,
    loadUserGroups,
    userGroups,
    userGroupsLoading
  } = useLearnerCategories();

  // Don't show categories dialog if there are no categories.
  // Fetch `userGroups`, only if there are some categories.
  useEffect(() => {
    if (!loadingCategories) {
      if (allCategories?.length) {
        loadUserGroups();
      } else {
        props.setDialogOpen(false);
      }
    }
  }, [allCategories]);

  useEffect(() => {
    if (!userGroupsLoading && userGroups) {
      // Actual user groups except `autojoin`.
      const actualUserGroups = userGroups.filter((g) => !g.autojoin).length;
      if (actualUserGroups) {
        props.setDialogOpen(false);
      } else {
        props.setDialogOpen(true);
      }
    }
  }, [userGroups]);

  const enableSave = allCategories?.every((c) => !c.category.assets.required);

  const requiredCategories = allCategories?.filter(
    (c) => c.category.assets.required
  );

  /**
   *  Enable `save` button when following conditions are met:
   * - If all categories are optional.
   * - All required categories item(s) are selected.
   */
  const canSubmit = (): boolean => {
    if (enableSave) return true;

    const singleSelectFilled = requiredCategories
      ?.filter((c) => c.category.assets.type === CategoryTypeEnum.SingleSelect)
      .every((c) =>
        c.items.some((i) => i.itemkey === singleSelectKey?.itemkey)
      );

    const multiSelectFilled = requiredCategories
      ?.filter((c) => c.category.assets.type === CategoryTypeEnum.MultiSelect)
      .every((c) =>
        c.items.some((i) =>
          multipleSelectKeys.some((item) => item.itemkey === i.itemkey)
        )
      );

    return singleSelectFilled && multiSelectFilled;
  };

  const handleSubmit = async () => {
    let resultItemKeys: CategoryItemKey[] = multipleSelectKeys;
    if (singleSelectKey) {
      resultItemKeys.push(singleSelectKey);
    }

    const response = await saveSelection(multipleSelectKeys);
    if (response?.ok && !saveError) {
      if (location.pathname === '/dashboard/course') {
        // Get all pathways/courses, if user is on `/dashboard/course` page.
        getPathways(userkey);
      }
      props.setDialogOpen(false);
    }
  };

  const handleMultiSelect = (itemkey: number) => {
    const copyItemKeys = cloneDeep(multipleSelectKeys);
    const itemIndex = copyItemKeys.findIndex((i) => i.itemkey === itemkey);
    if (itemIndex < 0) {
      copyItemKeys.push({ itemkey });
    } else {
      copyItemKeys.splice(itemIndex, 1);
    }
    setMultipleSelectKeys(copyItemKeys);
  };

  const handleItemSelect = (
    event: ChangeEvent<HTMLInputElement>,
    type: CategoryTypeEnum
  ) => {
    const itemkey = +event.target.value;
    if (type === CategoryTypeEnum.MultiSelect) {
      handleMultiSelect(itemkey);
    } else {
      setSingleSelectKey({ itemkey });
    }
  };

  const handleReset = () => {
    setSingleSelectKey(undefined);
    setMultipleSelectKeys([]);
  };

  return (
    <>
      {(loadingCategories || userGroupsLoading) && (
        <CustomLoadingProgress style={{ paddingTop: 100 }} />
      )}
      <Dialog open={props.open || false} fullWidth maxWidth="sm">
        <DialogTitle>Please complete this form</DialogTitle>
        <DialogContent>
          {loadingCategories || userGroupsLoading ? (
            <CustomLoadingProgress />
          ) : loadError ? (
            <ErrorPanel title="There was an error while loading categories." />
          ) : (
            <Grid container direction="column" spacing={2}>
              {allCategories?.map((c, index) => (
                <Grid key={index}>
                  <Typography variant="overline" lineHeight={1}>
                    {c.category.assets.required ? 'required' : `optional`}
                  </Typography>
                  <Typography variant="h6" color={main}>
                    {c.category.assets.label}
                  </Typography>

                  <RadioGroup name="single-selection-items">
                    {c.items.map((i) => (
                      <FormControlLabel
                        key={i.itemkey}
                        value={i.itemkey}
                        control={
                          c.category.assets.type ===
                          CategoryTypeEnum.MultiSelect ? (
                            <Checkbox
                              color="primary"
                              checked={
                                !!multipleSelectKeys.find(
                                  (item) => item.itemkey === i.itemkey
                                )
                              }
                            />
                          ) : (
                            <Radio
                              color="primary"
                              name="atEnd"
                              checked={singleSelectKey?.itemkey === i.itemkey}
                            />
                          )
                        }
                        label={i.assets.label}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleItemSelect(e, c.category.assets.type)
                        }
                        sx={{
                          '& .MuiRadio-root, .MuiCheckbox-root': {
                            padding: '6px'
                          }
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            disabled={!(multipleSelectKeys.length || singleSelectKey)}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={!canSubmit() || saving}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
